/**
 * /customer/promotions — All Promotions list (Phase W-2).
 *
 * Mirrors the mobile spec: two sections — "Active now" then
 * "Past campaigns" (omitted when empty). Past cards are dimmed
 * (opacity ~0.55) and carry a red-tinted EXPIRED pill.
 *
 * Data source: `customerPromotions(token, { include_expired: true })`.
 * The backend already splits + sorts; we just bucket by `is_expired`.
 */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Megaphone } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

function formatValidity(p) {
  // Compact display:
  //   active   — "Until 29 Jun"
  //   expired  — "Ended 23 May"
  const end = p.valid_until;
  if (!end) return '';
  const d = new Date(end);
  if (Number.isNaN(d.getTime())) return end;
  const short = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  return p.is_expired ? `Ended ${short}` : `Until ${short}`;
}

export default function CustomerPromotionsPage() {
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();

  const promosQ = useQuery({
    queryKey: qk.customerPromotions({ include_expired: true }),
    queryFn: () =>
      api.customerPromotions(token, { include_expired: true }),
    enabled: !!token,
  });

  const { active, expired } = useMemo(() => {
    const rows = promosQ.data ?? [];
    return {
      active: rows.filter((r) => !r.is_expired),
      expired: rows.filter((r) => r.is_expired),
    };
  }, [promosQ.data]);

  return (
    <div className="space-y-8" data-testid="customer-promotions-screen">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="customer-promotions-back"
          aria-label="Go back"
          className="w-10 h-10 rounded-full border border-white/10 bg-[#15151B] grid place-items-center text-white/70 hover:text-white hover:border-white/20 transition-colors shrink-0"
        >
          <ChevronLeft size={18} />
        </button>
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold">
            Promotions
          </h1>
          <p className="text-sm text-white/55 mt-1">
            All current and past campaigns across your brands
          </p>
        </div>
      </div>

      {promosQ.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-40 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : promosQ.error ? (
        <div className="rounded-2xl border border-white/10 bg-[#15151B] p-8 text-center">
          <div className="text-sm font-medium">Could not load promotions</div>
          <button
            onClick={() => promosQ.refetch()}
            className="text-xs font-medium text-gold mt-3 hover:underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Active section */}
          <section data-testid="customer-promotions-active-section">
            <SectionHeader title="Active now" count={active.length} />
            {active.length === 0 ? (
              <div
                className="rounded-2xl border border-white/10 bg-[#15151B] p-8 text-center"
                data-testid="customer-promotions-active-empty"
              >
                <Megaphone size={22} className="mx-auto text-gold/55 mb-2" />
                <div className="text-sm font-medium">
                  No active promotions right now
                </div>
                <div className="text-xs text-white/45 mt-1">
                  Check back soon — new campaigns drop every few weeks.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {active.map((p) => (
                  <PromoListCard key={p.id} promo={p} expired={false} />
                ))}
              </div>
            )}
          </section>

          {/* Past campaigns (omit when empty) */}
          {expired.length > 0 ? (
            <section data-testid="customer-promotions-past-section">
              <SectionHeader title="Past campaigns" count={expired.length} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {expired.map((p) => (
                  <PromoListCard key={p.id} promo={p} expired />
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function SectionHeader({ title, count }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <span className="text-xs font-mono text-gold uppercase tracking-wider">
        {count}
      </span>
    </div>
  );
}

function PromoListCard({ promo, expired }) {
  return (
    <Link
      to={`/customer/promotions/${promo.id}`}
      data-testid={`customer-promo-card-${promo.id}`}
      className={`group relative block rounded-2xl overflow-hidden border border-white/10 bg-[#15151B] hover:border-gold/35 transition-colors ${
        expired ? 'opacity-55 hover:opacity-70' : ''
      }`}
    >
      <div className="aspect-[16/9] relative bg-black/40">
        {promo.image_url ? (
          <img
            src={resolveImageUrl(promo.image_url)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        {expired ? (
          <div
            className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-rose-500/15 border border-rose-400/55 text-[10px] font-mono uppercase tracking-[0.16em] text-rose-300"
            data-testid="customer-promo-card-expired-badge"
          >
            Expired
          </div>
        ) : null}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-gold mb-1">
            {promo.brand_name}
          </div>
          <div className="font-display text-lg font-semibold leading-tight">
            {promo.title}
          </div>
        </div>
      </div>
      <div className="px-4 py-3">
        <p className="text-xs text-white/65 line-clamp-2 mb-2">
          {promo.description}
        </p>
        <div className="text-[10px] uppercase tracking-wider font-mono text-white/40">
          {formatValidity(promo)}
        </div>
      </div>
    </Link>
  );
}
